import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/hooks/useDeviceLive";

const Tile = ({ label, value, accent }) => (
  <div className="bg-kl-black p-4">
    <div className="kl-label-dark">{label}</div>
    <div className={`font-mono text-2xl mt-1 ${accent || ""}`}>{value}</div>
  </div>
);

export default function AdminOverview() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/admin/stats").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useInvalidate(["tenants", "devices", "orders", "reports", "history"], load, { admin: true });

  if (!data) return <div className="h-full flex items-center justify-center kl-label">Loading…</div>;
  const { totals, tenants } = data;

  return (
    <div className="h-full overflow-y-auto kl-scroll-light p-8" data-testid="admin-overview">
      <p className="kl-label">Platform</p>
      <h1 className="font-display font-light text-3xl mt-1">Overview</h1>
      <p className="text-sm text-kl-mutedLight mt-1">Every dealer's devices, activity and history at a glance.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-kl-line border border-kl-line mt-6 text-white">
        <Tile label="Dealers" value={totals.tenants} />
        <Tile label="Devices" value={totals.devices} />
        <Tile label="Active" value={totals.active} accent="text-kl-active" />
        <Tile label="Inactive" value={totals.inactive} accent="text-kl-inactive" />
        <Tile label="History" value={totals.history} />
        <Tile label="Proposals sent" value={totals.reports_sent} accent="text-kl-green" />
      </div>

      <h2 className="font-display text-xl mt-10 mb-3">Per dealer</h2>
      <div className="border border-kl-lineLight">
        <div className="grid grid-cols-[1.6fr_repeat(5,1fr)] gap-2 px-4 h-10 items-center kl-label border-b border-kl-lineLight bg-kl-light">
          <span>Dealer</span><span className="text-right">Devices</span><span className="text-right">Active</span><span className="text-right">Inactive</span><span className="text-right">History</span><span className="text-right">Sent</span>
        </div>
        {tenants.length === 0 && <p className="p-4 text-sm text-kl-mutedLight">No dealers yet.</p>}
        {tenants.map((t) => (
          <button
            key={t.tenant_id}
            onClick={() => nav(`/devices?tenant=${t.tenant_id}`)}
            data-testid={`admin-tenant-row-${t.tenant_id}`}
            className="w-full grid grid-cols-[1.6fr_repeat(5,1fr)] gap-2 px-4 h-12 items-center text-sm border-b border-kl-lineLight last:border-0 hover:bg-kl-light transition-colors text-left"
          >
            <span className="font-display truncate">{t.tenant_name}</span>
            <span className="text-right font-mono">{t.devices}</span>
            <span className="text-right font-mono text-kl-greenDark">{t.active}</span>
            <span className="text-right font-mono text-kl-inactive">{t.inactive}</span>
            <span className="text-right font-mono">{t.history}</span>
            <span className="text-right font-mono">{t.reports_sent}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
